// Closed vocabularies shared by the API (Mongoose enums, Zod schemas) and the web app (labels, filters).

export const ROLES = ["owner", "admin", "manager", "media_buyer", "agent", "viewer"] as const;
export type Role = (typeof ROLES)[number];

export const ORDER_STATUSES = [
  "new",
  "confirmed",
  "preparing",
  "shipped",
  "delivered",
  "returned",
  "cancelled",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const CONFIRMATION_STATES = [
  "pending",
  "requested",
  "no_answer",
  "postponed",
  "confirmed",
  "refused",
  "unreachable",
] as const;
export type ConfirmationState = (typeof CONFIRMATION_STATES)[number];

export const CONFIRMATION_METHODS = ["agent", "bot", "customer", "auto"] as const;
export type ConfirmationMethod = (typeof CONFIRMATION_METHODS)[number];

export const CANCEL_REASONS = [
  "customer_refused",
  "unreachable",
  "duplicate",
  "fake_order",
  "out_of_stock",
  "customer_request",
  "address_issue",
  "other",
] as const;
export type CancelReason = (typeof CANCEL_REASONS)[number];

export const PAYMENT_METHODS = ["cod", "card", "bank_transfer", "other"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_STATUSES = ["unpaid", "paid", "partially_refunded", "refunded"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const COD_STATES = ["none", "pending", "collected", "settled"] as const;
export type CodState = (typeof COD_STATES)[number];

export const ORDER_SOURCES = [
  "manual",
  "whatsapp",
  "messenger",
  "instagram",
  "website_chat",
  "landing_form",
  "meta_lead",
  "tiktok_lead",
  "import",
  "phone",
  "api",
] as const;
export type OrderSource = (typeof ORDER_SOURCES)[number];

/** Acquisition channel used by "Ventes par canal" and attribution. */
export const ATTRIBUTION_CHANNELS = ["meta_ads", "tiktok_ads", "google_ads", "organic", "other"] as const;
export type AttributionChannel = (typeof ATTRIBUTION_CHANNELS)[number];

export const SHIPMENT_STATUSES = [
  "pending",
  "picked_up",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "failed_attempt",
  "returning",
  "returned",
  "cancelled",
  "exception",
  "unknown",
] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

export const TERMINAL_SHIPMENT_STATUSES: readonly ShipmentStatus[] = ["delivered", "returned", "cancelled"];

export const CHANNELS = ["whatsapp", "messenger", "instagram", "website"] as const;
export type Channel = (typeof CHANNELS)[number];

export const CUSTOMER_SOURCES = [
  "whatsapp",
  "messenger",
  "instagram",
  "website",
  "landing_form",
  "meta_lead",
  "tiktok_lead",
  "manual",
  "import",
  "phone",
] as const;
export type CustomerSource = (typeof CUSTOMER_SOURCES)[number];

export const CONVERSATION_STATUSES = ["open", "pending", "resolved", "snoozed"] as const;
export type ConversationStatus = (typeof CONVERSATION_STATUSES)[number];

export const AI_MODES = ["auto", "assist", "off"] as const;
export type AiMode = (typeof AI_MODES)[number];

export const MESSAGE_DIRECTIONS = ["inbound", "outbound"] as const;
export type MessageDirection = (typeof MESSAGE_DIRECTIONS)[number];

export const MESSAGE_SENDERS = ["customer", "agent", "ai", "system", "automation"] as const;
export type MessageSender = (typeof MESSAGE_SENDERS)[number];

export const MESSAGE_TYPES = [
  "text",
  "image",
  "video",
  "audio",
  "file",
  "template",
  "interactive",
  "location",
  "sticker",
  "reaction",
  "note",
  "unsupported",
] as const;
export type MessageType = (typeof MESSAGE_TYPES)[number];

export const MESSAGE_STATUSES = ["received", "queued", "sent", "delivered", "read", "failed"] as const;
export type MessageStatus = (typeof MESSAGE_STATUSES)[number];

export const AD_PLATFORMS = ["meta", "tiktok", "google"] as const;
export type AdPlatform = (typeof AD_PLATFORMS)[number];

export const CAMPAIGN_OBJECTIVES = [
  "sales",
  "leads",
  "traffic",
  "awareness",
  "engagement",
  "messages",
  "app_promotion",
  "video_views",
] as const;
export type CampaignObjective = (typeof CAMPAIGN_OBJECTIVES)[number];

export const AD_ENTITY_STATUSES = [
  "draft",
  "active",
  "paused",
  "in_review",
  "rejected",
  "with_issues",
  "completed",
  "archived",
  "deleted",
  "error",
] as const;
export type AdEntityStatus = (typeof AD_ENTITY_STATUSES)[number];

export const AD_LEVELS = ["account", "campaign", "adset", "ad"] as const;
export type AdLevel = (typeof AD_LEVELS)[number];

export const AUDIENCE_TYPES = ["customer_list", "website", "engagement", "lookalike", "saved", "other"] as const;
export type AudienceType = (typeof AUDIENCE_TYPES)[number];

export const CREATIVE_TYPES = ["image", "video", "carousel", "text"] as const;
export type CreativeType = (typeof CREATIVE_TYPES)[number];

export const RECOMMENDATION_TYPES = [
  "increase_budget",
  "decrease_budget",
  "pause_entity",
  "activate_entity",
  "duplicate_winner",
  "refresh_creative",
  "new_audience",
  "lookalike_audience",
  "retargeting",
  "fix_tracking",
  "shift_budget",
  "stop_loss",
  "test_creative",
  "pause_out_of_stock",
  "insight",
] as const;
export type RecommendationType = (typeof RECOMMENDATION_TYPES)[number];

export const RECOMMENDATION_STATUSES = [
  "open",
  "approved",
  "rejected",
  "executing",
  "executed",
  "failed",
  "expired",
  "dismissed",
] as const;
export type RecommendationStatus = (typeof RECOMMENDATION_STATUSES)[number];

export const RISK_CLASSES = ["safe", "external", "financial"] as const;
export type RiskClass = (typeof RISK_CLASSES)[number];

export const ALERT_TYPES = [
  "spend_spike",
  "spend_drop",
  "roas_drop",
  "cpa_spike",
  "ctr_drop",
  "frequency_high",
  "creative_fatigue",
  "ad_rejected",
  "account_disabled",
  "budget_exhausted",
  "delivery_stopped",
  "token_expired",
  "pixel_inactive",
  "stockout_with_active_ads",
  "learning_limited",
  "guardrail_breach",
] as const;
export type AlertType = (typeof ALERT_TYPES)[number];

export const ALERT_SEVERITIES = ["info", "warning", "critical"] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

export const ALERT_STATUSES = ["open", "acknowledged", "resolved"] as const;
export type AlertStatus = (typeof ALERT_STATUSES)[number];

export const EXPERIMENT_TYPES = ["creative", "audience", "budget", "placement", "landing_page", "offer"] as const;
export type ExperimentType = (typeof EXPERIMENT_TYPES)[number];

export const EXPERIMENT_STATUSES = ["draft", "running", "completed", "stopped"] as const;
export type ExperimentStatus = (typeof EXPERIMENT_STATUSES)[number];

export const EXPERIMENT_METRICS = ["roas", "cpa", "ctr", "conversion_rate", "revenue_per_visitor"] as const;
export type ExperimentMetric = (typeof EXPERIMENT_METRICS)[number];

export const LEAD_STATUSES = ["new", "contacted", "qualified", "converted", "lost"] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

export const LEAD_SOURCES = ["landing_form", "meta_lead", "tiktok_lead", "website_chat", "manual"] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];

export const RETURN_TYPES = ["rto", "customer_return", "exchange"] as const;
export type ReturnType = (typeof RETURN_TYPES)[number];

export const RETURN_STATUSES = ["expected", "in_transit", "received", "restocked", "closed"] as const;
export type ReturnStatus = (typeof RETURN_STATUSES)[number];

export const SETTLEMENT_STATUSES = ["pending", "partial", "settled", "disputed"] as const;
export type SettlementStatus = (typeof SETTLEMENT_STATUSES)[number];

export const ONLINE_PAYMENT_PROVIDERS = ["konnect", "flouci"] as const;
export type OnlinePaymentProvider = (typeof ONLINE_PAYMENT_PROVIDERS)[number];

export const ONLINE_PAYMENT_STATUSES = ["created", "pending", "paid", "failed", "expired", "refunded"] as const;
export type OnlinePaymentStatus = (typeof ONLINE_PAYMENT_STATUSES)[number];

export const FOLLOWUP_TYPES = [
  "order_confirmation",
  "unconfirmed_order",
  "abandoned_form",
  "failed_delivery",
  "shipping_update",
  "review_request",
  "replenishment",
  "win_back",
  "payment_reminder",
  "custom",
] as const;
export type FollowupType = (typeof FOLLOWUP_TYPES)[number];

export const FOLLOWUP_STATUSES = ["scheduled", "sent", "failed", "cancelled", "replied", "skipped"] as const;
export type FollowupStatus = (typeof FOLLOWUP_STATUSES)[number];

export const PLAYBOOK_TRIGGERS = [
  "order.created",
  "order.confirmed",
  "order.cancelled",
  "order.status_changed",
  "shipment.status_changed",
  "message.received",
  "conversation.created",
  "lead.created",
  "customer.created",
  "form.abandoned",
  "product.low_stock",
  "ads.alert",
  "schedule.daily",
] as const;
export type PlaybookTrigger = (typeof PLAYBOOK_TRIGGERS)[number];

export const PLAYBOOK_ACTIONS = [
  "send_message",
  "send_template",
  "ai_reply",
  "create_shipment",
  "set_order_status",
  "request_confirmation",
  "add_tag",
  "remove_tag",
  "assign_conversation",
  "set_conversation_status",
  "create_followup",
  "notify_team",
  "webhook",
  "wait",
] as const;
export type PlaybookAction = (typeof PLAYBOOK_ACTIONS)[number];

export const CONDITION_OPERATORS = [
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "contains",
  "not_contains",
  "in",
  "not_in",
  "exists",
  "not_exists",
] as const;
export type ConditionOperator = (typeof CONDITION_OPERATORS)[number];

export const AI_PROVIDERS = ["anthropic", "openai", "gemini"] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];

export const AI_FEATURES = [
  "assistant",
  "autoreply",
  "media_buyer",
  "insights",
  "content",
  "brain_playground",
  "recommendations",
] as const;
export type AiFeature = (typeof AI_FEATURES)[number];

export const SUBSCRIPTION_STATUSES = ["trialing", "active", "past_due", "canceled", "expired"] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const INTEGRATION_PROVIDERS = ["meta", "tiktok", "google_ads"] as const;
export type IntegrationProvider = (typeof INTEGRATION_PROVIDERS)[number];

export const CONNECTION_STATUSES = ["active", "expired", "revoked", "error", "pending"] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

export const CARRIER_CONNECTION_STATUSES = ["pending", "active", "failed", "disabled"] as const;
export type CarrierConnectionStatus = (typeof CARRIER_CONNECTION_STATUSES)[number];

export const SITE_STATUSES = ["draft", "published", "archived"] as const;
export type SiteStatus = (typeof SITE_STATUSES)[number];

export const DOMAIN_STATUSES = ["pending", "verified", "failed"] as const;
export type DomainStatus = (typeof DOMAIN_STATUSES)[number];

export const SITE_EVENT_TYPES = [
  "page_view",
  "view_content",
  "add_to_cart",
  "initiate_checkout",
  "lead",
  "purchase",
  "contact",
] as const;
export type SiteEventType = (typeof SITE_EVENT_TYPES)[number];

export const BLOCK_TYPES = [
  "hero",
  "product",
  "benefits",
  "features",
  "testimonials",
  "faq",
  "order_form",
  "lead_form",
  "countdown",
  "gallery",
  "video",
  "cta",
  "text",
  "image",
  "trust_badges",
  "bundle_offer",
  "whatsapp_button",
  "spacer",
  "footer",
] as const;
export type BlockType = (typeof BLOCK_TYPES)[number];

export const REPORT_TYPES = ["sales", "orders", "deliveries", "ads", "customers", "products", "conversations", "cod"] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

export const REPORT_FORMATS = ["csv", "pdf"] as const;
export type ReportFormat = (typeof REPORT_FORMATS)[number];

export const NOTIFICATION_TYPES = [
  "order.created",
  "order.confirmed",
  "conversation.handoff",
  "shipment.exception",
  "ads.alert",
  "recommendation.created",
  "ai.quota_warning",
  "ai.quota_exceeded",
  "integration.error",
  "report.ready",
  "system",
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const ACTOR_TYPES = ["user", "ai", "system", "integration", "customer"] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

export const DATE_PRESETS = ["today", "yesterday", "7d", "30d", "90d", "this_month", "last_month", "custom"] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];
